import { getRbacRoute } from '@/entities/session';
import { useUserPreferencesStore } from '@/entities/user-preferences';
import { LogoutModal } from '@/features/logout';
import { Icon, type IconName } from '@/shared/assets/icons';
import {
  PROTECTED_ROUTE,
  SIDEBAR_CONFIG,
} from '@/shared/constants/sidebar-config';
import { cn } from '@/shared/lib/cn';
import { useSingleFacilityUser } from '@/shared/lib/hooks/use-single-facility-user';
import { Typography } from '@/shared/ui';
import NiceModal from '@ebay/nice-modal-react';
import {
  Link,
  useRouterState,
  type ToPathOption,
} from '@tanstack/react-router';
import { useMemo, type FC, type ReactNode } from 'react';

type SidebarItemProps = {
  icon: IconName;
  href?: ToPathOption;
  label?: ReactNode;
  isActive?: boolean;
  className?: string;
  onClick?: () => void;
};

const SidebarItem: FC<SidebarItemProps> = ({
  isActive = false,
  icon,
  href,
  label,
  className,
  onClick,
}) => {
  const sidebarIsOpened = useUserPreferencesStore(
    (state) => state.sidebarIsOpened
  );

  const Component = href ? Link : 'button';

  return (
    <Component
      to={href}
      className={cn(
        'flex items-center gap-2.5 py-3 transition-all duration-100 ease-in-out',
        isActive && 'bg-primary-6',
        isActive ? 'text-primary-inverse' : 'text-neutral-12',
        sidebarIsOpened ? 'px-6' : 'px-8',
        className
      )}
      onClick={onClick}
    >
      <Icon
        className="transition-all duration-300 ease-in-out group-first:rotate-180"
        name={icon}
      />
      <Typography
        variant="body"
        weight="medium"
        className={cn(
          'overflow-hidden text-left whitespace-nowrap transition-all duration-300 ease-in-out',
          sidebarIsOpened ? 'min-w-[120px]' : 'min-w-0'
        )}
      >
        {label}
      </Typography>
    </Component>
  );
};

export const Sidebar: FC = () => {
  const isOpened = useUserPreferencesStore((state) => state.sidebarIsOpened);
  const toggleSidebar = useUserPreferencesStore((state) => state.toggleSidebar);
  const { isSingleFacilityUser } = useSingleFacilityUser();

  /*
   TODO: search better solution for getting matched pathnames in tanstack router
   1) remove O(n^2)
   2) remove __root__ match
  */
  const matchedPathnames = useRouterState({
    select: (state) =>
      state.matches
        .filter(({ id }) => id !== '__root__')
        .map(({ pathname }) => pathname),
  });

  const sidebarItems: SidebarItemProps[] = useMemo(
    () =>
      SIDEBAR_CONFIG.map((page) => ({
        ...page,
        label:
          page.href === PROTECTED_ROUTE.FACILITIES && isSingleFacilityUser
            ? 'Main'
            : page.label,
        isActive: matchedPathnames.some((matchedPathName) =>
          matchedPathName.includes(page.href)
        ),
      })).filter(({ href }) => getRbacRoute(href).allowed),
    [matchedPathnames, isSingleFacilityUser]
  );

  return (
    <nav
      className={cn(
        'bg-primary-2 flex h-[calc(100vh-90px)] flex-col gap-2 py-2 transition-all duration-300 ease-in-out',
        isOpened ? 'w-[180px]' : 'w-[80px]'
      )}
    >
      <SidebarItem
        icon="chevron-left"
        className={cn(!isOpened && 'group')}
        onClick={toggleSidebar}
      />

      <div className="flex flex-1 flex-col">
        {sidebarItems.map((item) => (
          <SidebarItem
            key={item.icon}
            href={item.href}
            icon={item.icon}
            label={item.label}
            isActive={item.isActive}
          />
        ))}

        <SidebarItem
          icon="exit"
          label="Log Out"
          onClick={() => NiceModal.show(LogoutModal)}
        />
      </div>
    </nav>
  );
};
